import { useState, useEffect, useRef, useMemo } from "react"
import { Chart } from "react-chartjs-2"
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  BarController,
  PointElement,
  LineElement,
  LineController,
  Title,
  Tooltip,
  Legend,
  Filler,
} from "chart.js"
import API from "../../../utils/API"
import Sweetalert from "../../../utils/Sweetalert"
import ButtonCollapse from "../../ButtonCollapse"
import Select from "../../Select"
import { SPINNER_DELAY_MS } from "../constants"
import "../AiTargetingStats.css"

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  BarController,
  PointElement,
  LineElement,
  LineController,
  Title,
  Tooltip,
  Legend,
  Filler
)

const yAxisHorizontalTitlePlugin = {
  id: "yAxisHorizontalTitle",
  afterDraw(chart) {
    const yScale = chart.scales.y
    if (!yScale) return

    const titleOpts = yScale.options.title
    if (!titleOpts?.text) return

    const { ctx } = chart
    const font = titleOpts.font || {}

    ctx.save()
    ctx.font = `${font.weight || "600"} ${font.size || 12}px sans-serif`
    ctx.fillStyle = titleOpts.color || "#495057"
    ctx.textAlign = "left"
    ctx.textBaseline = "bottom"
    ctx.fillText(titleOpts.text, yScale.left, yScale.top - 12)
    ctx.restore()
  },
}

const GRANULARITY_OPTIONS = [
  { id: "hour", label: "Heure" },
  { id: "day", label: "Jour" },
  { id: "week", label: "Semaine" },
]

const GENDER_OPTIONS = [
  { key: null, label: "Tous" },
  { key: "M", label: "Hommes" },
  { key: "F", label: "Femmes" },
]

const AGE_RANGE_OPTIONS = [
  { id: "0-12", name: "0-12 ans" },
  { id: "13-17", name: "13-17 ans" },
  { id: "18-24", name: "18-24 ans" },
  { id: "25-34", name: "25-34 ans" },
  { id: "35-44", name: "35-44 ans" },
  { id: "45-54", name: "45-54 ans" },
  { id: "55-64", name: "55-64 ans" },
  { id: "65+", name: "65+ ans" },
]

function formatLabel(raw, granularity, singleDay) {
  if (granularity === "hour") {
    const d = new Date(raw)

    if (isNaN(d)) return raw

    if (singleDay) {
      return d.toLocaleString("fr-FR", { hour: "2-digit", minute: "2-digit" })
    }

    return d.toLocaleString("fr-FR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" })
  }

  if (granularity === "day") {
    const d = new Date(raw)

    if (isNaN(d)) return raw

    return d.toLocaleDateString("fr-FR", { day: "2-digit", month: "short" })
  }

  return raw
}

export default function TrafficTrendChart({ queryParams }) {
  const [data, setData] = useState(null)
  const [showSpinner, setShowSpinner] = useState(true)
  const [granularity, setGranularity] = useState("day")
  const spinnerTimerRef = useRef(null)
  const hasPreviousData = useRef(false)

  const [selectedGender, setSelectedGender] = useState(null)
  const [selectedAgeRanges, setSelectedAgeRanges] = useState(AGE_RANGE_OPTIONS)

  const isSingleDay = useMemo(() => {
    const params = new URLSearchParams(queryParams)
    const startDate = params.get("startDate")
    const endDate = params.get("endDate")

    if (!startDate || !endDate) return false

    const start = new Date(startDate)
    const end = new Date(endDate)

    return (
      start.getFullYear() === end.getFullYear() &&
      start.getMonth() === end.getMonth() &&
      start.getDate() === end.getDate()
    )
  }, [queryParams])

  const isDailyProfile = useMemo(() => {
    if (granularity !== "hour") return false

    return !isSingleDay
  }, [granularity, isSingleDay])

  const trendQueryParams = useMemo(() => {
    const params = new URLSearchParams(queryParams)
    params.set("granularity", isDailyProfile ? "dailyProfile" : granularity)

    if (selectedGender) {
      params.set("gender", selectedGender)
    }

    const isSubset =
      selectedAgeRanges.length > 0 &&
      selectedAgeRanges.length < AGE_RANGE_OPTIONS.length
    if (isSubset) {
      params.set("ageRanges", selectedAgeRanges.map((opt) => opt.id).join(","))
    }

    return params.toString()
  }, [queryParams, granularity, isDailyProfile, selectedGender, selectedAgeRanges])

  useEffect(() => {
    const controller = new AbortController()

    clearTimeout(spinnerTimerRef.current)

    if (!hasPreviousData.current) {
      setShowSpinner(true)
    } else {
      spinnerTimerRef.current = setTimeout(() => setShowSpinner(true), SPINNER_DELAY_MS)
    }

    API.GET(
      `/ai-targeting-stats/traffic-trend?${trendQueryParams}`,
      (response) => {
        clearTimeout(spinnerTimerRef.current)
        setData(response.data)
        setShowSpinner(false)
        hasPreviousData.current = true
      },
      () => {
        clearTimeout(spinnerTimerRef.current)
        Sweetalert.fireErrorToast("Impossible de charger la tendance du trafic")
        setData(null)
        setShowSpinner(false)
      },
      { signal: controller.signal }
    )

    return () => {
      controller.abort()
      clearTimeout(spinnerTimerRef.current)
    }
  }, [trendQueryParams])

  const chartData = useMemo(() => {
    if (!data || data.length === 0) return null

    const counts = data.map((d) => d.count)

    return {
      labels: data.map((d) => isDailyProfile ? d.label : formatLabel(d.label, granularity, isSingleDay)),
      datasets: [
        {
          type: "bar",
          label: "Nombre de passages",
          data: counts,
          backgroundColor: "rgba(0, 123, 255, 0.25)",
          hoverBackgroundColor: "rgba(0, 123, 255, 0.45)",
          borderColor: "rgba(0, 123, 255, 0.4)",
          borderWidth: 1,
          borderRadius: 3,
          order: 2,
        },
        {
          type: "line",
          label: "Tendance",
          data: counts,
          borderColor: "#007bff",
          backgroundColor: "transparent",
          borderWidth: 2,
          tension: 0.3,
          pointRadius: data.length > 60 ? 0 : 3,
          pointHoverRadius: 5,
          pointBackgroundColor: "#007bff",
          order: 1,
        },
      ],
    }
  }, [data, granularity, isDailyProfile, isSingleDay])

  const xAxisTitle = useMemo(() => {
    if (isDailyProfile) return "Heure de la journée"
    switch (granularity) {
      case "hour": return "Créneau horaire"
      case "week": return "Semaine"
      default: return "Date"
    }
  }, [granularity, isDailyProfile])

  const barValuePlugin = useMemo(
    () => ({
      id: "barValueLabels",
      afterDatasetsDraw(chart) {
        const meta = chart.getDatasetMeta(0)
        if (!meta || meta.type !== "bar" || meta.hidden) return

        const { ctx } = chart
        const barCount = meta.data.length
        const fontSize = barCount > 30 ? 11 : barCount > 16 ? 12 : 13
        const topPadding = 15

        ctx.save()
        ctx.font = `600 ${fontSize}px sans-serif`
        ctx.fillStyle = "#495057"
        ctx.textAlign = "center"
        ctx.textBaseline = "top"

        for (const bar of meta.data) {
          const value = chart.data.datasets[0].data[bar.$context.index]
          if (value == null || value === 0) continue

          const barHeight = bar.base - bar.y
          if (barHeight < fontSize + topPadding + 4) continue

          ctx.fillText(value, bar.x, bar.y + topPadding)
        }

        ctx.restore()
      },
    }),
    []
  )

  const chartOptions = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      layout: { padding: { top: 25 } },
      plugins: {
        legend: { display: false },
        tooltip: {
          filter: (item) => item.datasetIndex === 0,
          callbacks: {
            title: (items) => items[0]?.label || "",
            label: isDailyProfile
              ? (item) => `~${item.raw} passage${item.raw > 1 ? "s" : ""} en moyenne`
              : (item) => `${item.raw} passage${item.raw > 1 ? "s" : ""}`,
          },
        },
      },
      scales: {
        x: {
          title: {
            display: true,
            align: "end",
            text: xAxisTitle,
            font: { size: 12, weight: "600" },
            color: "#495057",
            padding: { top: 8 },
          },
          ticks: {
            maxRotation: 45,
            maxTicksLimit: 20,
            font: { size: 11 },
          },
          grid: { display: false },
        },
        y: {
          title: {
            display: false,
            text: isDailyProfile ? "Moyenne de passages" : "Nombre de passages",
            font: { size: 12, weight: "600" },
            color: "#495057",
          },
          beginAtZero: true,
          ticks: {
            precision: 0,
            font: { size: 11 },
          },
        },
      },
    }),
    [xAxisTitle, isDailyProfile]
  )

  return (
    <div className="row">
      <div className="col">
        <div className="card card-primary card-outline">
          <div className="card-header">
            <h3 className="card-title">
              <i className="fas fa-chart-line mr-2"></i>
              Tendance du trafic
            </h3>
            <div className="card-tools">
              <ButtonCollapse />
            </div>
          </div>
          <div className="card-body" style={{ display: "block" }}>
            <div className="ai-stats-info-banner mb-3">
              <i className="fas fa-info-circle ai-stats-info-banner__icon"></i>
              <small>
                Évolution du nombre de personnes distinctes passant devant les écrans au fil du temps.
                Utilisez les filtres ci-dessous pour affiner l'analyse.
              </small>
            </div>
            <div className="ai-stats-filter-bar">
              <div className="ai-stats-filter-bar__group">
                <label className="ai-stats-filter-bar__label">Granularité</label>
                <div className="btn-group" role="group">
                  {GRANULARITY_OPTIONS.map((opt) => (
                    <button
                      key={opt.id}
                      type="button"
                      className={`btn btn-sm ${granularity === opt.id ? "btn-primary" : "btn-outline-primary"}`}
                      onClick={() => setGranularity(opt.id)}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>
              <div className="ai-stats-filter-bar__group">
                <label className="ai-stats-filter-bar__label">Genre</label>
                <div className="btn-group" role="group">
                  {GENDER_OPTIONS.map((opt) => (
                    <button
                      key={opt.key ?? "all"}
                      type="button"
                      className={`btn btn-sm ${selectedGender === opt.key ? "btn-primary" : "btn-outline-primary"}`}
                      onClick={() => setSelectedGender(opt.key)}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>
              <div className="ai-stats-filter-bar__group">
                <label className="ai-stats-filter-bar__label">Tranches d'âge</label>
                <div className="ai-stats-filter-bar__age-select">
                  <Select
                    onChange={(options) => setSelectedAgeRanges(options || [])}
                    options={AGE_RANGE_OPTIONS}
                    defaultValue={AGE_RANGE_OPTIONS}
                    isMulti={true}
                    name="ageRangeFilter"
                  />
                </div>
              </div>
            </div>

            {showSpinner ? (
              <div className="ai-stats-spinner ai-stats-chart-container">
                <i className="fas fa-2x fa-sync-alt fa-spin"></i>
              </div>
            ) : !chartData ? (
              <div className="ai-stats-empty ai-stats-chart-container">
                <div className="ai-stats-empty__inner">
                  <i className="fas fa-chart-line ai-stats-empty__icon"></i>
                  <p className="ai-stats-empty__text">Aucune donnée disponible pour cette période.</p>
                </div>
              </div>
            ) : (
              <div className="ai-stats-chart-container">
                <Chart type="bar" data={chartData} options={chartOptions} plugins={[barValuePlugin, yAxisHorizontalTitlePlugin]} />
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
